import React, { useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  IconButton,
  Tooltip,
  CircularProgress,
  Typography,
  Popover,
} from "@mui/material";
import {
  EditOutlined,
  DeleteOutline,
  SendOutlined,
  KeyboardArrowDownRounded,
} from "@mui/icons-material";
import {
  Booking,
  FrontendStatus,
  PaymentStatus,
} from "../../../types/bookings";
import { DocumentIcon } from "../../../components/icons";
import PaymentStatusDropdown from "./PaymentStatusDropdown";

interface StreamListTableProps {
  bookings: Booking[];
  loading: boolean;
  onRowClick: (id: number) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => void;
  onMemorialPage: (id: number) => void;
  onSendProposal: (id: number) => void;
  onStatusChange?: (id: number, newStatus: FrontendStatus) => void;
  hasEditPermission: boolean;
  hasDeletePermission: boolean;
  onPaymentStatusChange?: (id: number, newStatus: PaymentStatus) => void;
}

const STATUS_OPTIONS: FrontendStatus[] = [
  "enquiry",
  "confirmed",
  "fulfilled",
  "cancelled",
  "lost",
  "testing",
];

const getStatusChipStyles = (status: FrontendStatus) => {
  switch (status) {
    case "confirmed":
      return {
        backgroundColor: "#C8E6C9",
        color: "#2E7D32",
        label: "Confirmed",
      };
    case "fulfilled":
      return {
        backgroundColor: "#C5CAE9",
        color: "#283593",
        label: "Fulfilled",
      };
    case "enquiry":
      return {
        backgroundColor: "#FFE0B2",
        color: "#E65100",
        label: "Enquiry",
      };
    case "cancelled":
      return {
        backgroundColor: "#FFCDD2",
        color: "#C62828",
        label: "Cancelled",
      };
    case "lost":
      return {
        backgroundColor: "#EEEEEE",
        color: "#616161",
        label: "Lost",
      };
    case "testing":
      return {
        backgroundColor: "#D7CCC8",
        color: "#4E342E",
        label: "Testing",
      };
    default:
      return {
        backgroundColor: "#E0E0E0",
        color: "#424242",
        label: status,
      };
  }
};

interface StatusDropdownProps {
  bookingId: number;
  currentStatus: FrontendStatus;
  onStatusChange?: (id: number, newStatus: FrontendStatus) => void;
}

const StatusDropdown: React.FC<StatusDropdownProps> = ({
  bookingId,
  currentStatus,
  onStatusChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleSelect = (e: React.MouseEvent, status: FrontendStatus) => {
    e.stopPropagation();
    onStatusChange?.(bookingId, status);
    setAnchorEl(null);
  };

  const currentStyles = getStatusChipStyles(currentStatus);

  return (
    <>
      {/* Trigger chip */}
      <Box
        onClick={handleOpen}
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: "4px",
          px: "12px",
          py: "5px",
          borderRadius: "999px",
          backgroundColor: currentStyles.backgroundColor,
          color: currentStyles.color,
          cursor: "pointer",
          fontWeight: 600,
          fontSize: "12px",
          userSelect: "none",
          transition: "filter 0.15s ease",
          "&:hover": { filter: "brightness(0.95)" },
        }}
      >
        {currentStyles.label}
        <KeyboardArrowDownRounded
          sx={{
            fontSize: "16px",
            transition: "transform 0.2s ease",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {/* Dropdown popover */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={(e) => handleClose(e as React.MouseEvent)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            elevation: 4,
            sx: {
              mt: "6px",
              borderRadius: "16px",
              p: "10px",
              minWidth: "180px",
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        {STATUS_OPTIONS.map((status) => {
          const styles = getStatusChipStyles(status);
          const isSelected = status === currentStatus;

          return (
            <Box
              key={status}
              onClick={(e) => handleSelect(e, status)}
              sx={{
                px: "16px",
                py: "8px",
                borderRadius: "999px",
                backgroundColor: styles.backgroundColor,
                color: styles.color,
                fontWeight: isSelected ? 700 : 500,
                fontSize: "13px",
                textAlign: "center",
                cursor: "pointer",
                outline: isSelected ? `2px solid ${styles.color}` : "none",
                outlineOffset: "-2px",
                transition: "filter 0.15s ease, transform 0.1s ease",
                "&:hover": {
                  filter: "brightness(0.93)",
                  transform: "scale(1.02)",
                },
              }}
            >
              {styles.label}
            </Box>
          );
        })}
      </Popover>
    </>
  );
};

const StreamListTable: React.FC<StreamListTableProps> = ({
  bookings,
  loading,
  onRowClick,
  onEdit,
  onDelete,
  onMemorialPage,
  onSendProposal,
  onStatusChange,
  hasEditPermission,
  hasDeletePermission,
  onPaymentStatusChange,
}) => {
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress sx={{ color: "#4285F4" }} />
      </Box>
    );
  }

  if (bookings.length === 0) {
    return (
      <Box textAlign="center" py={4}>
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          No bookings found
        </Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      sx={{ borderRadius: "12px", overflow: "hidden" }}
    >
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#F8F9FA" }}>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Stream Title
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Date
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Package
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Payment Status
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {bookings.map((booking) => (
            <TableRow
              key={booking.id}
              hover
              sx={{
                cursor: "pointer",
                "&:hover": { backgroundColor: "background.paper" },
              }}
              onClick={() => onRowClick(booking.id)}
            >
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {booking.title}
                    </Typography>
                    {booking.companyBranch && (
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary", display: "block" }}
                      >
                        {booking.companyBranch}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  {booking.dateOfService}
                </Typography>
              </TableCell>

              <TableCell onClick={(e) => e.stopPropagation()}>
                <StatusDropdown
                  bookingId={booking.id}
                  currentStatus={booking.status}
                  onStatusChange={onStatusChange}
                />
              </TableCell>

              <TableCell>
                <Typography variant="body2" sx={{ color: "text.primary" }}>
                  {booking.package}
                </Typography>
              </TableCell>
              <TableCell>
                <PaymentStatusDropdown
                  bookingId={booking.id}
                  currentStatus={booking.paymentStatus}
                  onPaymentStatusChange={onPaymentStatusChange}
                />
              </TableCell>

              <TableCell>
                <Box
                  sx={{ display: "flex", gap: 0.5 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {booking.status === "enquiry" && (
                    <Tooltip title="Send Proposal" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onSendProposal(booking.id)}
                        sx={{ color: "#614438" }}
                      >
                        <SendOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasEditPermission && (
                    <Tooltip title="Edit" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onEdit(booking.id)}
                        sx={{ color: "text.secondary" }}
                      >
                        <EditOutlined fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title="Memorial Page" arrow>
                    <IconButton
                      size="small"
                      onClick={() => onMemorialPage(booking.id)}
                      sx={{ color: "#614438" }}
                    >
                      <DocumentIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {hasDeletePermission && (
                    <Tooltip title="Delete" arrow>
                      <IconButton
                        size="small"
                        onClick={() => onDelete(booking.id)}
                        sx={{ color: "#F44336" }}
                      >
                        <DeleteOutline fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StreamListTable;
